/**
 * Centralized nutrition calculation utilities.
 * Single source of truth for the Atwater energy formula and all derived values.
 *
 * Energy formula: Protein×4 + (Carbs − Fiber)×4 + Fat×9
 *   digestibleCarbs = max(0, carbs − fiber)  → prevents negative values when fiber > carbs
 */

/** kcal → kJ conversion factor */
export const KCAL_TO_KJ = 4.184;

/**
 * Calculate energy (kcal) using the Atwater formula.
 * Fiber is subtracted from carbohydrates to get digestible carbs.
 * All negative inputs are treated as 0. Fiber is capped at carbs to prevent
 * negative digestible carbs from producing invalid negative energy.
 *
 * @param {number|null} protein - grams
 * @param {number|null} carbs   - grams total carbohydrate
 * @param {number|null} fiber   - grams fiber (reduces digestible carbs, capped at carbs)
 * @param {number|null} fat     - grams
 * @returns {number} energy in kcal (≥ 0), rounded to 1 decimal
 */
export function calculateEnergyKcal(protein, carbs, fiber, fat) {
  const p  = Math.max(0, +protein || 0);
  const c  = Math.max(0, +carbs   || 0);
  const fi = Math.max(0, Math.min(+fiber || 0, c)); // fiber cannot exceed carbs
  const fa = Math.max(0, +fat     || 0);
  const digestibleCarbs = c - fi;
  return Math.round((p * 4 + digestibleCarbs * 4 + fa * 9) * 10) / 10;
}

/**
 * Convert kcal to kJ.
 * @param {number|null} kcal
 * @returns {number|null} kJ rounded to nearest integer, or null if input is invalid
 */
export function kcalToKj(kcal) {
  if (kcal == null || !Number.isFinite(+kcal)) return null;
  return Math.round(+kcal * KCAL_TO_KJ);
}

/**
 * Compute a per-100g value from a per-serving value and serving size.
 * Returns null on divide-by-zero, missing inputs, or non-positive serving size.
 *
 * @param {number|null} perServingValue
 * @param {number}      servingSizeG    - must be > 0
 * @returns {number|null}
 */
export function calcPer100g(perServingValue, servingSizeG) {
  if (perServingValue == null) return null;
  const sg = +servingSizeG;
  if (!Number.isFinite(sg) || sg <= 0) return null;
  return (+perServingValue / sg) * 100;
}

/**
 * Apply per-100g factor to every key in a nutrients object.
 * Returns null if serving size is invalid.
 *
 * @param {object|null} perServingNutrients - { energy, protein, fat, ... }
 * @param {number}      servingSizeG
 * @returns {object|null}
 */
export function calcNutrientsPer100g(perServingNutrients, servingSizeG) {
  if (!perServingNutrients) return null;
  const sg = +servingSizeG;
  if (!Number.isFinite(sg) || sg <= 0) return null;
  const factor = 100 / sg;
  const result = {};
  for (const [k, v] of Object.entries(perServingNutrients)) {
    result[k] = v != null ? v * factor : null;
  }
  return result;
}

/**
 * Round a nutrient value for storage/display.
 * @param {number|null} value
 * @param {number}      decimals - default 1
 * @returns {number|null}
 */
export function roundNutrient(value, decimals = 1) {
  if (value == null || !Number.isFinite(+value)) return null;
  const m = 10 ** decimals;
  return Math.round(+value * m) / m;
}

/**
 * Format a nutrient for display. Returns '—' for null/undefined/NaN.
 * @param {number|null} value
 * @param {number}      decimals - default 1
 * @returns {string}
 */
export function fmtNut(value, decimals = 1) {
  const r = roundNutrient(value, decimals);
  if (r == null) return '—';
  return r.toFixed(decimals);
}

/**
 * Validate entered energy against the Atwater formula (±5% tolerance).
 * Returns { ok: true } when any input is missing (non-blocking).
 *
 * @param {number|string|null} energy
 * @param {number|string|null} protein
 * @param {number|string|null} fat
 * @param {number|string|null} carb
 * @param {number|string|null} fiber - default 0
 * @returns {{ ok: boolean, expected?: number, entered?: number }}
 */
export function checkCaloriesMatch(energy, protein, fat, carb, fiber = 0) {
  if (energy == null || protein == null || fat == null || carb == null) return { ok: true };
  const e  = +energy;
  const p  = +protein;
  const f  = +fat;
  const c  = +carb;
  const fi = +fiber || 0;
  if ([e, p, f, c].some((v) => isNaN(v))) return { ok: true };
  const netCarbs = Math.max(0, c - fi);
  const expected = p * 4 + netCarbs * 4 + f * 9;
  if (expected <= 0) return { ok: true };
  const diff = Math.abs(e - expected);
  return { ok: diff <= expected * 0.05, expected, entered: e };
}

/**
 * Scale a nutrients-per-100g object by grams/100 to get the contribution
 * of `grams` of this ingredient.
 *
 * @param {object} nutrients100g - { energy_per_100g, protein_per_100g, ... }
 * @param {number} grams         - quantity of ingredient in grams
 * @returns {object} scaled values (same key names)
 */
export function scaleNutrients100g(nutrients100g, grams) {
  if (!nutrients100g || !grams) return {};
  const factor = grams / 100;
  const result = {};
  for (const [k, v] of Object.entries(nutrients100g)) {
    result[k] = v != null ? +v * factor : null;
  }
  return result;
}

/**
 * Sum two partial nutrient objects. Null fields are preserved as null
 * only when both sides are null; otherwise they are treated as 0.
 *
 * @param {object} a
 * @param {object} b
 * @returns {object}
 */
export function addNutrients(a, b) {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  const result = {};
  for (const k of keys) {
    const va = (a || {})[k];
    const vb = (b || {})[k];
    if (va == null && vb == null) {
      result[k] = null;
    } else {
      result[k] = (+va || 0) + (+vb || 0);
    }
  }
  return result;
}

/**
 * Divide every key of a nutrient object by a divisor (e.g. yield_amount for per-serving).
 * Null fields remain null.
 *
 * @param {object} nutrients
 * @param {number} divisor   - must be > 0
 * @returns {object}
 */
export function divideNutrients(nutrients, divisor) {
  if (!nutrients || !divisor || divisor <= 0) return nutrients || {};
  const result = {};
  for (const [k, v] of Object.entries(nutrients)) {
    result[k] = v != null ? v / divisor : null;
  }
  return result;
}

/**
 * Round every value in a nutrients object to `decimals` places.
 * @param {object} nutrients
 * @param {number} decimals - default 1
 * @returns {object}
 */
export function roundNutrients(nutrients, decimals = 1) {
  if (!nutrients) return {};
  const result = {};
  for (const [k, v] of Object.entries(nutrients)) {
    result[k] = roundNutrient(v, decimals);
  }
  return result;
}
